class AnniversaryBrandHistory extends HTMLElement {
  connectedCallback() {
    if (this.abortController && !this.abortController.signal.aborted) return
    this.panel = this.querySelector('[data-history-panel]')
    this.content = this.querySelector('[data-history-content]')
    this.viewport = this.querySelector('[data-history-viewport]')
    this.track = this.querySelector('[data-history-track]')
    if (!this.panel || !this.content || !this.viewport || !this.track) return

    this.abortController = new AbortController()
    this.activeIndex = -1
    this.items = []
    this.frame = 0
    this.desktop = matchMedia('(min-width: 1000px)')
    this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)')
    const { signal } = this.abortController
    this.scheduleUpdate = () => {
      if (this.frame || signal.aborted) return
      this.frame = requestAnimationFrame(() => {
        this.frame = 0
        if (!signal.aborted && this.isConnected) this.update()
      })
    }
    this.measureLayout = () => this.measure()
    window.addEventListener('scroll', this.scheduleUpdate, { passive: true, signal })
    window.addEventListener('resize', this.measureLayout, { passive: true, signal })
    this.desktop.addEventListener('change', this.measureLayout, { signal })
    this.reducedMotion.addEventListener('change', this.measureLayout, { signal })
    this.viewport.addEventListener('keydown', (event) => this.onKeydown(event), { signal })
    this.addEventListener(
      'shopify:block:select',
      (event) => {
        const index = this.items.findIndex((item) => item.dataset.blockId === event.detail?.blockId)
        if (index !== -1) this.selectMilestone(index)
      },
      { signal },
    )
    document.addEventListener(
      'shopify:section:unload',
      (event) => {
        if (event.target?.contains?.(this)) this.disconnectedCallback()
      },
      { signal },
    )
    this.resizeObserver = new ResizeObserver(this.measureLayout)
    this.resizeObserver.observe(this.panel)
    this.resizeObserver.observe(this.content)
    this.resizeObserver.observe(this.viewport)
    this.resizeObserver.observe(document.documentElement)
    this.mutationObserver = new MutationObserver(() => this.refresh())
    this.mutationObserver.observe(this.track, { childList: true })
    this.refresh()
  }

  refresh() {
    if (this.abortController?.signal.aborted) return
    const active = this.items[this.activeIndex]
    this.items = [...this.querySelectorAll('[data-history-milestone]')].filter(
      (item) => !item.hidden,
    )
    const activeRemoved = active && !this.items.includes(active)
    this.activeIndex = -1
    this.measure()
    if (activeRemoved && this.items.length) this.selectMilestone(0)
  }

  measure() {
    if (!this.abortController || this.abortController.signal.aborted) return
    // A resize can clamp scrollY before this callback. Keep the last rendered
    // reading phase instead of deriving the old phase from the new layout.
    const previousProgress = this.progress
    const focusedHistory = [...document.querySelectorAll('anniversary-brand-history')].find(
      (history) => {
        if (!history.contains(document.activeElement)) return false
        const row = history.readingRow
        return row && row.bottom > 0 && row.top < this.viewportHeight
      },
    )
    // Keep keyboard reading at either endpoint too. Unfocused sections only
    // retain progress while occupying the reading area; distant instances
    // must never scroll the reader back to themselves.
    const preserveProgress =
      this.scrollDriven &&
      previousProgress >= -1 / this.scrollRange &&
      previousProgress <= 1 + 1 / this.scrollRange &&
      (focusedHistory
        ? focusedHistory === this
        : this.readingRootTop < this.viewportHeight / 2 &&
          this.readingRootTop + this.panelHeight + this.distance + this.scrollLead >
            this.viewportHeight / 2)
    const previousGeometry = this.geometry
    const chrome = getComputedStyle(document.documentElement)
    const number = (name) => parseFloat(chrome.getPropertyValue(name)) || 0
    // Read the hosts' stable height inputs, not the header's scroll visibility.
    this.readingTop =
      number('--sticky-header-enabled') * number('--header-height') +
      number('--sticky-announcement-bar-enabled') * number('--announcement-bar-height')
    this.viewportHeight = window.innerHeight
    const contentHeight = this.content.offsetHeight
    const rowHeight = this.viewport.offsetHeight
    // Measure inside the content group, independently of the panel padding.
    // Otherwise each ResizeObserver delivery would move its own measurement.
    const rowOffset =
      this.viewport.getBoundingClientRect().top - this.content.getBoundingClientRect().top
    const availableHeight = Math.max(0, this.viewportHeight - this.readingTop)
    const maxPadding = availableHeight - rowOffset - rowHeight - 24
    const moving = this.items.length > 1 && !this.reducedMotion.matches
    this.mode =
      !moving || (this.desktop.matches && (this.items.length < 6 || maxPadding < 24))
        ? 'flow'
        : this.desktop.matches
          ? 'pinned'
          : 'mobile'
    this.dataset.historyMode = this.mode
    this.scrollDriven = this.mode === 'pinned'
    const offset = Math.max(-120, Math.min(120, Number(this.dataset.desktopVerticalOffset) || 0))
    const desiredTop =
      this.dataset.desktopContentPosition === 'top' ? 120 : (availableHeight - contentHeight) / 2
    const contentTop = Math.max(24, Math.min(maxPadding, desiredTop + offset))
    // Keep the complete composition together. Native sticky reaches the reading
    // position without changing the content's natural 120px inset during entry.
    this.stickyTop = this.readingTop + contentTop - 120
    this.style.setProperty('--history-viewport-height', `${this.viewportHeight - this.stickyTop}px`)
    this.style.setProperty('--history-content-top', `${contentTop}px`)
    this.style.setProperty(
      '--history-background-extension',
      `${Math.max(0, this.stickyTop - this.readingTop)}px`,
    )
    this.panelHeight = this.panel.getBoundingClientRect().height
    this.pitch =
      this.items.length > 1 ? Math.max(1, this.items[1].offsetLeft - this.items[0].offsetLeft) : 312
    this.distance = this.mode === 'pinned' ? (this.items.length - 1) * this.pitch : 0
    const first = this.items[0]
    const visibleSlots =
      this.viewport.clientWidth >= 1920
        ? 5
        : Math.max(
            3,
            Math.floor(
              (this.viewport.clientWidth - (first?.offsetLeft ?? 0) - (first?.offsetWidth ?? 280)) /
                this.pitch,
            ) + 1,
          )
    this.endShift = Math.max(0, this.items.length - Math.min(5, visibleSlots)) * this.pitch
    this.firstOffset = first?.offsetLeft ?? 0
    this.middleAnchor =
      this.viewport.clientWidth >= 1920
        ? this.firstOffset + 2 * this.pitch
        : (this.viewport.clientWidth - (first?.offsetWidth ?? 280)) / 2
    // A section near the page top may already be sticky at scrollY=0. Reserve
    // the clipped entrance so Home/End still span the full milestone distance.
    const documentTop = window.scrollY + this.getBoundingClientRect().top
    this.scrollLead = this.scrollDriven ? Math.max(0, this.stickyTop - documentTop) : 0
    this.scrollStartOffset = -this.stickyTop + this.scrollLead
    this.scrollRange = this.distance
    this.style.setProperty('--history-scroll-distance', `${this.distance}px`)
    this.style.setProperty('--history-scroll-lead', `${this.scrollLead}px`)
    this.style.setProperty('--history-panel-height', `${this.panelHeight}px`)
    this.style.setProperty('--history-sticky-top', `${this.stickyTop}px`)
    this.geometry = [
      this.mode,
      this.viewport.clientWidth,
      this.viewportHeight,
      this.panelHeight,
      contentTop,
      this.scrollStartOffset,
      this.scrollRange,
    ].join(':')
    if (this.scrollDriven) this.viewport.scrollLeft = 0
    if (preserveProgress && this.scrollDriven && previousGeometry !== this.geometry) {
      this.scrollToPhase(Math.max(0, Math.min(1, previousProgress)) * (this.items.length - 1))
    }
    this.update()
  }

  update() {
    if (!this.abortController || this.abortController.signal.aborted) return
    this.readingRootTop = this.getBoundingClientRect().top
    this.readingRow = this.viewport.getBoundingClientRect()
    this.progress = this.scrollDriven
      ? (-this.readingRootTop - this.scrollStartOffset) / this.scrollRange
      : 0
    let index = -1
    let shift = 0
    if (this.scrollDriven) {
      const phase = Math.max(
        0,
        Math.min(this.items.length - 1, this.progress * (this.items.length - 1)),
      )
      index = Math.round(phase)
      const middleEnd = this.items.length - 4
      if (this.items.length === 6) {
        shift = Math.max(0, Math.min(1, phase - 2)) * this.endShift
      } else if (phase <= 2) {
        shift = 0
      } else if (phase < 3) {
        shift = (phase - 2) * (this.firstOffset + 3 * this.pitch - this.middleAnchor)
      } else if (phase <= middleEnd) {
        shift = this.firstOffset + phase * this.pitch - this.middleAnchor
      } else if (phase < middleEnd + 1) {
        const middleShift = this.firstOffset + middleEnd * this.pitch - this.middleAnchor
        shift = middleShift + (phase - middleEnd) * (this.endShift - middleShift)
      } else {
        shift = this.endShift
      }
    } else if (this.mode === 'mobile') {
      let nearest = Infinity
      this.items.forEach((item, position) => {
        const rect = item.getBoundingClientRect()
        const distance = Math.abs(rect.top + rect.height / 2 - window.innerHeight / 2)
        if (distance < nearest) {
          nearest = distance
          index = position
        }
      })
    }
    this.style.setProperty('--history-shift', `${shift ? -Math.round(shift * 100) / 100 : 0}px`)
    this.setActive(index)
  }

  setActive(index) {
    if (index === this.activeIndex) return
    this.activeIndex = index
    this.items.forEach((item, position) => {
      if (position === index) item.setAttribute('aria-current', 'step')
      else item.removeAttribute('aria-current')
    })
  }

  onKeydown(event) {
    if (!this.scrollDriven || event.target !== this.viewport) return
    const targets = {
      ArrowLeft: this.activeIndex - 1,
      ArrowRight: this.activeIndex + 1,
      Home: 0,
      End: this.items.length - 1,
    }
    if (!(event.key in targets)) return
    event.preventDefault()
    this.selectMilestone(targets[event.key])
  }

  selectMilestone(index) {
    if (!this.items.length) return
    const target = Math.max(0, Math.min(this.items.length - 1, index))
    if (this.scrollDriven) {
      this.scrollToPhase(target)
      this.update()
    } else {
      this.items[target].scrollIntoView({ behavior: 'auto', block: 'center', inline: 'center' })
      if (this.mode === 'mobile') this.setActive(target)
    }
  }

  scrollToPhase(phase) {
    window.scrollTo({
      top:
        window.scrollY +
        this.getBoundingClientRect().top +
        this.scrollStartOffset +
        (phase / (this.items.length - 1)) * this.scrollRange,
      behavior: 'auto',
    })
  }

  disconnectedCallback() {
    this.abortController?.abort()
    this.resizeObserver?.disconnect()
    this.mutationObserver?.disconnect()
    if (this.frame) cancelAnimationFrame(this.frame)
    this.frame = 0
    this.items?.forEach((item) => item.removeAttribute('aria-current'))
    this.activeIndex = -1
    this.scrollDriven = false
    this.geometry = null
    delete this.dataset.historyMode
    for (const property of [
      '--history-scroll-distance',
      '--history-scroll-lead',
      '--history-panel-height',
      '--history-sticky-top',
      '--history-viewport-height',
      '--history-content-top',
      '--history-background-extension',
      '--history-shift',
    ]) {
      this.style.removeProperty(property)
    }
    this.abortController = null
    this.resizeObserver = null
    this.mutationObserver = null
  }
}

if (!customElements.get('anniversary-brand-history')) {
  customElements.define('anniversary-brand-history', AnniversaryBrandHistory)
}
